import type { Integration, IntegrationConfig, IntegrationMetadata } from '../types/integration';
import type { IntegrationInstallation } from '../types/integrationInstallation';
import type { IntegrationsStore } from '../types/integrationsStore';

import { Log } from '@devanjs/log';
import { merge } from 'lodash';
import { ref } from 'vue';
import { services } from '../services';
import type { PossiblyAsyncIntegration } from '../types/asyncIntegration';
import { installMenu } from './installMenu';
import { installRoute } from './installRoute';

export type InstallOptions = {
  setupAt?: Date;
  metadata?: IntegrationMetadata;
  preInstallHook?: () => Promise<any>;
  postInstallHook?: () => Promise<any>;
};

export const install = async <
  I extends Integration,
  C extends IntegrationConfig<I>,
  Integrations extends Record<string, PossiblyAsyncIntegration>,
>(
  store: IntegrationsStore<Integrations>,
  integration: I,
  config: (config: C) => C = (c) => c,
  options?: InstallOptions,
): Promise<IntegrationInstallation<I>> => {
  Log.tag('integrations').space().green('Installing').info(integration.code);

  const existingInstallation = store.installations[integration.code];

  if (existingInstallation !== undefined) {
    return existingInstallation as unknown as IntegrationInstallation<I>;
  }

  const promises: Promise<any>[] = [];

  const capture = <A extends any[], T>(fn: (...args: A) => T): ((...args: A) => T) => {
    return (...args) => {
      const response = fn(...args);

      if (response instanceof Promise) {
        promises.push(response);
      }

      return response;
    };
  };

  //TODO:@Ozz Ensure icon on menu item is editable and can be mapped to fontawesome icon
  const apiMetadata = options?.metadata
    ? { title: options.metadata.title, sortOrder: options.metadata.sortOrder }
    : undefined;

  const metadata: IntegrationMetadata | undefined =
    integration.metadata || apiMetadata ? merge({}, integration.metadata, apiMetadata) : undefined;

  const installation: IntegrationInstallation<I> = {
    integration,
    config: () => config(integration.config()),
    extendedConfig: integration.config(),
    routes: [],
    menu: [],
    metadata,
    setupAt: options?.setupAt,
  };

  /**
   * Clean up any extensions that might already exist from this integration.
   * ? Should never happen but just in case. Can occur with hot module reloading.
   */
  Object.assign(
    store.extensions,
    store.extensions.filter((i) => i.source.code !== integration.code),
  );

  const installing = (async () => {
    if (options?.preInstallHook) {
      await options?.preInstallHook();
    }

    /**
     * Execute the setup function for the integration.
     */
    Log.tag('integrations').space().green(integration.code).info('Setup');

    await integration.setup({
      use: (plugin, ...options) => {
        services.app?.use(plugin, ...options);
      },
      config: Object.assign((key?: string) => {
        if (key) {
          return installation.config()[key];
        }

        return installation.config();
      }, installation.config()),
      metadata,
      route: capture((route) => installRoute([installation], route)),
      menu: capture((item, condition) => installMenu([installation], item, condition)),
      extend: (name, setup, teardown) => {
        Log.tag('integrations')
          .space()
          .green(integration.code)
          .lightGreen('Extension')
          .text(name)
          .info();

        const installed = ref(false);

        if (typeof teardown === 'number') {
          capture((name, config, priority) => {
            store.extensions.push({
              source: integration,
              target: name,
              config,
              setup: () => {
                installed.value = true;
              },
              teardown: () => {
                installed.value = false;
              },
              priority,
            });
          })(name, setup, teardown);
        } else {
          capture((name, setup, teardown) => {
            store.extensions.push({
              source: integration,
              target: name,
              config: (c) => c,
              setup: (...args) => {
                setup?.(...args);
                installed.value = true;
              },
              teardown: (...args) => {
                teardown?.(...args);
                installed.value = false;
              },
              priority: 0,
            });
          })(name, setup, teardown);
        }

        return installed;
      },
    });

    /**
     * Wait for all collected promises to resolve before returning the installation.
     * ? This is to ensure that any async setup functions are completed before the integration is returned.
     */
    await Promise.all(promises);

    store.extensions
      .filter((extension) => {
        return (
          extension.target === installation.integration.code ||
          extension.source.code === installation.integration.code
        );
      })
      .forEach((extension) => {
        const targetInstallation = store.installations[extension.target];

        if (targetInstallation !== undefined) {
          extension.setup({
            menu: (item, condition) =>
              installMenu([installation, targetInstallation], item, condition),
            config: (config, priority) => {
              // @ts-expect-error Set read-only config
              extension.config = config;
              // @ts-expect-error Set read-only priority
              extension.priority = priority;
            },
          });
        }
      });

    Object.values(store.installations).forEach((installation) => {
      Object.assign(
        installation.extendedConfig,
        store.extensions
          .filter((extension) => extension.target === installation.integration.code)
          .reduce((config, extension) => extension.config(config), installation.config()),
      );
    });

    if (options?.postInstallHook) {
      await options?.postInstallHook();
    }
  })();

  // @ts-expect-error
  store.installations[integration.code] = installation;

  try {
    await installing;

    Log.tag('integrations').space().green('Installed').info(integration.code);
  } catch (error) {
    delete store.installations[integration.code];

    Log.tag('integrations').space().red('Install Failed').info(integration.code);

    throw error;
  }

  return installation;
};
